import React, { useEffect, useMemo, useCallback, useRef } from "react";
import { useFixedPhoneSlice } from "app/pages/Customer/FixedPhone/slice";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import type { FixedPhoneActivationParentOutletContext } from "../utils/fixed-phone-activation-outlet-context";
import {
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import {
  getActivationCurrentPlanTypeFromSim,
  getDeviceIncludedPlanResolution,
  getIncludedPlanHighlightForChooseStep,
  isActivationSimYearlyPlan,
  applyUpsellPlansToActivationStorage,
} from "../utils/activation-plan-helpers";

import { Button } from "app/components/ui/button";
import { Skeleton } from "app/components/ui/skeleton";

export const ChooseServicePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { useGetFixedPhoneUpsellPlansQuery } = useFixedPhoneSlice();
  const {
    data: plansData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetFixedPhoneUpsellPlansQuery({});
  const plans = useMemo(() => plansData?.data || [], [plansData]);

  const isRequestPending = isLoading || isFetching;
  /** Same pattern as congratulations-dialog / security-plan: act only after a settled response. */
  const canUsePlanActions =
    !isRequestPending && !isError && plansData !== undefined;

  const continueNavLockRef = useRef(false);
  const simData = getActivationFlowData()?.simData;
  const planInclusion = getDeviceIncludedPlanResolution(simData);
  const isNeitherIncluded = planInclusion === "neither";
  /** Seeds session plan ids / default selection; uses explicit device resolution (monthly default when both included). */
  const upsellSeedPlanType = getActivationCurrentPlanTypeFromSim(simData);
  /** "Included" UI + headings: null when device includes both monthly and yearly (user must choose). */
  const includedPlanHighlight = getIncludedPlanHighlightForChooseStep(simData);

  useEffect(() => {
    applyUpsellPlansToActivationStorage(plans, upsellSeedPlanType);
  }, [plans, upsellSeedPlanType]);

  useEffect(() => {
    if (location.pathname !== "/activation/fixed-phone/choose-service-plan")
      return;
    if (isActivationSimYearlyPlan(getActivationFlowData()?.simData)) {
      navigate("/activation/fixed-phone/security-plan-free-trial", {
        replace: true,
      });
    }
  }, [navigate, location.pathname]);

  const handleContinue = useCallback(
    (planId: string, durationLabel: string) => {
      if (continueNavLockRef.current) return;
      if (!canUsePlanActions) return;
      const id = String(planId || "").trim();
      if (!id) return;

      continueNavLockRef.current = true;
      try {
        saveActivationFlowData({
          planId: id,
          selectedPlanType: durationLabel === "monthly" ? "monthly" : "yearly",
        });
        navigate("/activation/fixed-phone/security-plan-free-trial");
      } catch {
        continueNavLockRef.current = false;
      }
    },
    [canUsePlanActions, navigate],
  );

  const getDurationLabel = (plan: any): "monthly" | "yearly" => {
    const title = String(plan?.title || "").toLowerCase();
    if (title.includes("yearly") || title.includes("annual")) return "yearly";
    return "monthly";
  };

  const getDisplayTitle = (plan: any): string => {
    const title = String(plan?.title || "");
    return title.replace(/^upgrade to\s+/i, "");
  };

  const getBadgeText = (plan: any): string => {
    const badge = String(plan?.badgeText || "").replace(/[^\x20-\x7E]/g, "");
    return badge.trim();
  };

  const showEmptyPlans =
    !isRequestPending &&
    !isError &&
    plansData !== undefined &&
    plans.length === 0;

  const { setActivationFlowFooterSuppressed } =
    useOutletContext<FixedPhoneActivationParentOutletContext>() ?? {};

  useEffect(() => {
    const suppressed = isError || showEmptyPlans;
    setActivationFlowFooterSuppressed?.(suppressed);
    return () => setActivationFlowFooterSuppressed?.(false);
  }, [isError, showEmptyPlans, setActivationFlowFooterSuppressed]);

  const getFeatureWeightClass = (feature: string): string => {
    const normalized = feature.trim().toLowerCase();
    const priorityFeatures = [
      "keep your existing phone number",
      "free calls to 170+ countries",
      "e911/911 emergency service",
      "nationwide coverage",
    ];
    if (normalized.includes("save")) {
      return "font-semibold";
    }
    if (priorityFeatures.some((priority) => normalized === priority)) {
      return "font-medium";
    }
    return "font-normal";
  };

  if (isError) {
    return (
      <div className="px-5 md:px-6 xl:px-7 2xl:pr-[1.875rem] py-4 sm:py-6 md:py-8 lg:py-10 h-[calc(100vh-13.75rem)]">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-5 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
            Failed to load Upsell pricing plans
          </h3>
          <p className="max-w-sm text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 sm:mb-4 lg:mb-5 mx-auto">
            {(error as any)?.data?.message ||
              "We couldn't retrieve the plans at this moment. Please try again."}
          </p>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (showEmptyPlans) {
    return (
      <div className="px-5 md:px-6 xl:px-7 2xl:pr-[1.875rem] py-4 sm:py-6 md:py-8 lg:py-10 h-[calc(100vh-13.75rem)]">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-5 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
            No service plans to show right now
          </h3>
          <p className="max-w-md text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 sm:mb-4 lg:mb-5 mx-auto">
            We didn&apos;t receive any plan options from our catalog. This may
            be temporary—try refreshing. If it keeps happening, contact support
            and we&apos;ll help you finish activation.
          </p>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="text-center mb-4 sm:mb-[1.125rem] lg:mb-5">
        <h2 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2vw+1rem),2.25rem)] font-normal leading-snug md:leading-normal lg:leading-[3.3125rem] mb-1 sm:mb-1.5 lg:mb-2">
          {includedPlanHighlight === "yearly"
            ? "Review Your Service Plan Options"
            : includedPlanHighlight === "monthly"
            ? "Upgrade to Annual & Save Big"
            : "Choose Your Service Plan"}
        </h2>
        <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base lg:text-lg font-normal leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7">
          {includedPlanHighlight === "yearly"
            ? "You are currently on Unlimited Yearly. Keep your plan or review monthly pricing below."
            : includedPlanHighlight === "monthly"
            ? "Keep your service active and avoid interruptions, one payment covers full year."
            : isNeitherIncluded
            ? "No plan is included with this device. Choose monthly or annual service below to continue."
            : "Your device includes monthly and yearly options. Pick the plan that works best for you."}
        </p>
      </div>

      <ul className="flex flex-wrap [@media(max-width:824px)]:flex-wrap-reverse gap-[1.125rem] justify-center w-full sm:gap-5 md:gap-6 xl:gap-8 2xl:gap-[2.375rem]">
          {((!plans || plans.length === 0) && isRequestPending
            ? Array.from({ length: 2 })
            : plans || []
          ).map((planRaw: any, index: number) => {
            const plan = planRaw || {};
            const isSkeleton = isRequestPending;
            const durationLabel = isSkeleton
              ? index === 0
                ? "monthly"
                : "yearly"
              : getDurationLabel(plan);
            const isCurrentPlan =
              includedPlanHighlight !== null &&
              durationLabel === includedPlanHighlight;
            const isMonthlyUnavailable =
              includedPlanHighlight === "yearly" && durationLabel === "monthly";
            const showUpgradePrefix =
              durationLabel === "yearly" && includedPlanHighlight === "monthly";

            return (
              <li
                key={plan._id || index}
                className="flex overflow-hidden relative flex-col w-full sm:w-[22.6875rem] max-w-[22.6875rem] rounded-2xl md:rounded-[1.25rem]"
              >
                {durationLabel === "yearly" && (
                  <div className="absolute top-0 left-0 bg-[#1D1D1B] w-full h-11 sm:h-[3.15rem] lg:h-[3.9375rem] rounded-t-[1.25rem] md:rounded-t-3xl" />
                )}
                <div
                  className={`relative z-[2] flex justify-center items-center text-center ${
                    index === 2
                      ? "[@media(min-width:1392px)]:h-10"
                      : index === 0
                      ? "[@media(min-width:825px)]:h-9 lg:h-10"
                      : "h-8 sm:h-9 lg:h-10"
                  }`}
                >
                  {!isSkeleton && durationLabel === "yearly" && (
                    <>
                      <span className="text-white text-xs sm:text-[0.825rem] lg:text-sm font-medium leading-[0.875rem] sm:leading-4 lg:leading-[1.125rem]">
                        {getBadgeText(plan)}
                      </span>
                      <img
                        src="/assets/crown.webp"
                        alt="Sparkles"
                        className="ml-1 -mt-[0.1875rem] sm:-mt-1 sm:ml-1.5 w-[1.1875rem] h-[0.6875rem] sm:w-[1.3063rem] sm:h-[0.7563rem] lg:w-[1.425rem] lg:h-[0.825rem]"
                      />
                    </>
                  )}
                  {isSkeleton && durationLabel === "yearly" && (
                    <>
                      <Skeleton className="w-16 sm:w-[4.375rem] lg:w-[4.75rem] h-3.5 sm:h-4 lg:h-[1.125rem] bg-white/20 rounded-md" />
                      <Skeleton className="ml-1 sm:ml-1.5 w-[1.1875rem] h-[0.825rem] sm:w-[1.3063rem] sm:h-[0.925rem] lg:w-[1.425rem] lg:h-4 bg-white/20 rounded" />
                    </>
                  )}
                </div>
                <div
                  className={`relative flex-1 flex flex-col justify-between bg-white w-full p-5 sm:p-6 md:px-[1.625rem] md:py-7 lg:py-8 rounded-2xl md:rounded-[1.25rem] ${
                    durationLabel === "yearly"
                      ? "border-x-2 border-b-2 border-dashed border-[#1D1D1B]"
                      : "border border-[#EBEBEB]"
                  }`}
                >
                  {durationLabel === "yearly" && (
                    <div className="absolute z-0 top-0 inset-x-0 h-3 sm:h-3.5 lg:h-4 bg-white rounded-t-2xl md:rounded-t-[1.25rem]" />
                  )}
                  <div className="flex-1 flex flex-col">
                    <h4 className="text-[#1D2530] font-mobile text-xl sm:text-[1.375rem] lg:text-2xl font-bold leading-6 sm:leading-7 lg:leading-8 whitespace-nowrap mb-1 sm:mb-[0.34rem]">
                      {isSkeleton ? (
                        <div className="flex items-center">
                          <Skeleton
                            className={`${
                              showUpgradePrefix
                                ? "w-64 sm:w-[17rem] lg:w-[18.75rem]"
                                : "w-[11.5rem] sm:w-[11.75rem] md:w-48"
                            } h-6 sm:h-7 lg:h-8 bg-[#E2E4E9] rounded-md mb-1 sm:mb-1.5`}
                          />
                          {isCurrentPlan && (
                            <Skeleton className="inline-block align-middle mt-1 ml-1 sm:ml-1.5 w-14 sm:w-[3.625rem] lg:w-[3.875rem] h-4 sm:h-5 bg-[#E2E4E9] rounded-md" />
                          )}
                        </div>
                      ) : (
                        <>
                          {(showUpgradePrefix ? "Upgrade to " : "") +
                            getDisplayTitle(plan)}
                          {isCurrentPlan && (
                            <span className="inline-block text-[#999999] font-inter text-[0.825rem] sm:text-sm lg:text-[0.925rem] font-normal leading-[0.875rem] sm:leading-4 lg:leading-[1.125rem] ml-1 sm:ml-1.5">
                              Included
                            </span>
                          )}
                        </>
                      )}
                    </h4>
                    {isSkeleton ? (
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-2.5 lg:mb-[0.725rem]">
                        <div className="flex gap-1 sm:gap-1.5 items-end">
                          <Skeleton
                            className={`${
                              index === 0
                                ? "w-[7.25rem] sm:w-[7.5rem] lg:w-[7.75rem]"
                                : "w-[9.5rem] sm:w-[9.75rem] lg:w-40"
                            } h-[2.125rem] sm:h-9 lg:h-10 bg-[#E2E4E9] rounded-md`}
                          />
                          <Skeleton
                            className={`${
                              index === 0
                                ? "w-5 sm:w-6 lg:w-7"
                                : "w-10 sm:w-11 lg:w-12"
                            } h-5 sm:h-6 lg:h-7 bg-[#E2E4E9] rounded-md pb-1`}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-2.5 mb-2 sm:mb-2.5 lg:mb-[0.725rem]">
                        {plan.originalPrice && (
                          <p className="font-mobile text-[#98A2B3] text-[2rem] sm:text-[2.125rem] lg:text-4xl font-normal leading-[2.125rem] sm:leading-9 lg:leading-10 line-through">
                            ${String(plan.originalPrice).replace("$", "")}
                          </p>
                        )}
                        <div className="flex gap-1">
                          <p className="font-mobile text-[#1D2530] text-[2rem] sm:text-[2.125rem] lg:text-4xl font-bold leading-[2.125rem] sm:leading-9 lg:leading-10">
                            ${plan.price}
                          </p>
                          {durationLabel === "monthly" && (
                            <span className="inline-block mt-auto pb-[0.1875rem] sm:pb-0.5 text-[#5C6F8A] text-[0.925rem] sm:text-base lg:text-lg font-normal leading-5 sm:leading-6 lg:leading-7">
                              /{durationLabel === "monthly" ? "mo" : "year"}
                            </span>
                          )}
                          {durationLabel === "yearly" && !isCurrentPlan && (
                            <span className="inline-block mt-auto pb-[0.1875rem] sm:pb-0.5 text-[#1D1D1B] text-sm sm:text-[0.90625rem] lg:text-[0.9375rem] font-normal leading-5 sm:leading-[1.375rem] lg:leading-6">
                              Today Only
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="text-[#141414] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.125rem] sm:leading-5 lg:leading-6 my-3.5 sm:my-4 lg:my-5">
                      {isSkeleton ? (
                        <div className="flex flex-col gap-1 sm:gap-1.5">
                          <Skeleton className="w-full h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md" />
                          <Skeleton
                            className={`${
                              index === 0 ? "w-full" : "w-2/3"
                            } h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md`}
                          />
                          <Skeleton
                            className={`${
                              index === 0 ? "w-1/2" : "hidden"
                            } h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md`}
                          />
                        </div>
                      ) : (
                        plan.description
                      )}
                    </div>
                    <ul className="space-y-1.5 sm:space-y-2 lg:space-y-2.5 mb-3.5 sm:mb-4 lg:mb-5">
                      {isSkeleton ? (
                        Array.from({ length: index === 0 ? 7 : 6 }).map(
                          (_, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                            >
                              <Skeleton className="shrink-0 w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5 bg-[#E2E4E9] rounded mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem]" />
                              <Skeleton className="w-[13.5rem] sm:w-[13.75rem] md:w-56 h-[1.375rem] sm:h-6 bg-[#E2E4E9] rounded-md" />
                            </li>
                          ),
                        )
                      ) : (
                        <>
                          {plan.features &&
                            plan.features.map(
                              (feature: string, featureIndex: number) => {
                                if (
                                  (durationLabel === "yearly" &&
                                    feature
                                      .toLowerCase()
                                      .includes("free months")) ||
                                  feature.toLowerCase().includes("50% off")
                                ) {
                                  return null;
                                }

                                return (
                                  <li
                                    key={featureIndex}
                                    className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                  >
                                    <span className="flex-shrink-0 mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem] text-[#1EC677]">
                                      <svg
                                        className="w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M2 12H22M22 12L15 7M22 12L15 17"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                    <p
                                      className={`text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base leading-5 sm:leading-6 md:leading-relaxed ${getFeatureWeightClass(
                                        feature,
                                      )}`}
                                    >
                                      {feature}
                                    </p>
                                  </li>
                                );
                              },
                            )}
                        </>
                      )}
                    </ul>
                  </div>
                  {isSkeleton ? (
                    <Skeleton className="w-full h-11 sm:h-[2.875rem] md:h-12 lg:h-14 bg-[#E2E4E9] rounded-[3.125rem]" />
                  ) : (
                    <Button
                      type="button"
                      variant={
                        isCurrentPlan && !isMonthlyUnavailable
                          ? "outline"
                          : "primary"
                      }
                      disabled={
                        isMonthlyUnavailable ||
                        (!isSkeleton && !canUsePlanActions)
                      }
                      className={`capitalize h-11 sm:h-[2.875rem] md:h-12 lg:h-14 rounded-[3.125rem] text-xs sm:text-[0.825rem] lg:text-sm font-semibold leading-3 sm:leading-[1.05rem] ${
                        isCurrentPlan && !isMonthlyUnavailable ? "" : ""
                      }`}
                      onClick={() =>
                        !isMonthlyUnavailable &&
                        canUsePlanActions &&
                        handleContinue(plan.homePhonePlanId, durationLabel)
                      }
                    >
                      {/* hover:bg-[#FAFAFA] border-[#EBEBEB] text-[#999999] */}
                      {/* text-[#141414] */}
                      {durationLabel === "monthly" ? (
                        isMonthlyUnavailable ? (
                          "Unavailable"
                        ) : isCurrentPlan ? (
                          plan.buttonText
                        ) : (
                          "Start Monthly Plan"
                        )
                      ) : isCurrentPlan ? (
                        "Keep Current Plan"
                      ) : isNeitherIncluded ? (
                        <>
                          <img
                            src="/assets/arrow-right-white-thick.webp"
                            alt="Arrow"
                            className="animate-nudge-right w-[1.0938rem] h-[0.7188rem] sm:w-[1.2031rem] sm:h-[0.7906rem] lg:w-[1.3125rem] lg:h-[0.8625rem] mr-2 sm:mr-2.5 lg:mr-3"
                          />
                          Start Annual Plan
                        </>
                      ) : (
                        <>
                          <img
                            src="/assets/arrow-right-white-thick.webp"
                            alt="Arrow"
                            className="animate-nudge-right w-[1.0938rem] h-[0.7188rem] sm:w-[1.2031rem] sm:h-[0.7906rem] lg:w-[1.3125rem] lg:h-[0.8625rem] mr-2 sm:mr-2.5 lg:mr-3"
                          />
                          {plan.buttonText}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
    </div>
  );
};
